import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { Header, Footer } from "./components/Layout";
// Legacy ChatWidget removed - replaced by AIChatWidget with Gemini AI
import AIChatWidget from "./components/AIChatWidget";
import NewsletterSubscribe from "./components/NewsletterSubscribe";
import { PWAInstallPrompt, OfflineIndicator, UpdateAvailableBanner } from "./components/PWAComponents";
import { VisitorTracker } from "./components/VisitorTracker";

// i18n initialization
import './i18n';

// Pages
import HomePage from "./pages/HomePage";
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const VerifyOTPPage = lazy(() => import("./pages/VerifyOTPPage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const ProductDetailPage = lazy(() => import("./pages/ProductDetailPage"));
const SuppliersPage = lazy(() => import("./pages/SuppliersPage"));
const SupplierDetailPage = lazy(() => import("./pages/SupplierDetailPage"));
const CategoriesPage = lazy(() => import("./pages/CategoriesPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const MyProductsPage = lazy(() => import("./pages/MyProductsPage"));
const SupplierToolsPage = lazy(() => import("./pages/SupplierToolsPage"));
const ProductFormPage = lazy(() => import("./pages/ProductFormPage"));
const RFQListPage = lazy(() => import("./pages/RFQListPage"));
const CreateRFQPage = lazy(() => import("./pages/CreateRFQPage"));
const RFQDetailPage = lazy(() => import("./pages/RFQDetailPage"));
const OrdersListPage = lazy(() => import("./pages/OrdersListPage"));
const OrderDetailPage = lazy(() => import("./pages/OrderDetailPage"));
const CreateOrderPage = lazy(() => import("./pages/CreateOrderPage"));
// PricingPage removed - using commission-based model
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const GuidesPage = lazy(() => import("./pages/GuidesPage"));
const TrackingPage = lazy(() => import("./pages/TrackingPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("./pages/PaymentCancelPage"));
// Escrow pages removed - using contact-based model
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const BecomeSupplierPage = lazy(() => import("./pages/BecomeSupplierPage"));
// SubscriptionPage removed - using commission-based model
const SellerAnalyticsPage = lazy(() => import("./pages/SellerAnalyticsPage"));
const DisputesPage = lazy(() => import("./pages/DisputesPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const MarketerRegisterPage = lazy(() => import("./pages/MarketerRegisterPage"));
const MarketerLoginPage = lazy(() => import("./pages/MarketerLoginPage"));
const MarketerDashboardPage = lazy(() => import("./pages/MarketerDashboardPage"));
const AffiliateTermsPage = lazy(() => import("./pages/AffiliateTermsPage"));
const AfCFTACheckerPage = lazy(() => import("./pages/AfCFTACheckerPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const MarketIntelligencePage = lazy(() => import("./pages/MarketIntelligencePage"));
const ReferralPartnerRegisterPage = lazy(() => import("./pages/ReferralPartnerRegisterPage"));
const ReferralPartnerLoginPage = lazy(() => import("./pages/ReferralPartnerLoginPage"));
const ReferralPartnerDashboard = lazy(() => import("./pages/ReferralPartnerDashboard"));

const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center bg-bone" data-testid="page-loader">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bone">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Layout (with Header/Footer)
const PublicLayout = () => {
  return (
    <>
      <Header />
      <main>
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
      <AIChatWidget />
      <NewsletterSubscribe variant="floating" />
    </>
  );
};

// Auth Layout (no Header/Footer)
const AuthLayout = () => {
  return <Suspense fallback={<PageLoader />}><Outlet /></Suspense>;
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
      <CurrencyProvider>
        <BrowserRouter>
          <VisitorTracker>
          {/* PWA Components */}
          <OfflineIndicator />
          <UpdateAvailableBanner />
          <PWAInstallPrompt />
          
          <Toaster 
            position="top-right" 
            richColors 
            closeButton
            toastOptions={{
              style: {
                fontFamily: 'Manrope, sans-serif'
              }
            }}
          />
          <Routes>
            {/* Auth Routes - No Header/Footer */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/verify-otp" element={<VerifyOTPPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              
              {/* Affiliate/Marketer Routes */}
              <Route path="/affiliate/register" element={<MarketerRegisterPage />} />
              <Route path="/affiliate/login" element={<MarketerLoginPage />} />
              <Route path="/affiliate/dashboard" element={<MarketerDashboardPage />} />
              <Route path="/affiliate/terms" element={<AffiliateTermsPage />} />
              
              {/* Referral Partner Routes */}
              <Route path="/partner/register" element={<ReferralPartnerRegisterPage />} />
              <Route path="/partner/login" element={<ReferralPartnerLoginPage />} />
              <Route path="/partner/dashboard" element={<ReferralPartnerDashboard />} />
            </Route>

            {/* Public Routes - With Header/Footer */}
            <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/become-a-supplier" element={<BecomeSupplierPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/:id" element={<ProductDetailPage />} />
            <Route path="/suppliers" element={<SuppliersPage />} />
            <Route path="/suppliers/:id" element={<SupplierDetailPage />} />
            <Route path="/categories" element={<CategoriesPage />} />
            <Route path="/guides" element={<GuidesPage />} />
            <Route path="/tracking" element={<TrackingPage />} />
            <Route path="/pricing" element={<HowItWorksPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            
            {/* AfCFTA Trade Tools */}
            <Route path="/tools/afcfta-checker" element={<AfCFTACheckerPage />} />
            
            {/* Market Intelligence */}
            <Route path="/market-intelligence" element={<MarketIntelligencePage />} />
            <Route path="/market-intelligence/:marketId" element={<MarketIntelligencePage />} />
          </Route>

          {/* Protected Dashboard Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}><DashboardPage /></Suspense>
              </ProtectedRoute>
            }
          >
            {/* Supplier Routes */}
            <Route path="products" element={<MyProductsPage />} />
            <Route path="products/create" element={<ProductFormPage />} />
            <Route path="products/edit/:id" element={<ProductFormPage />} />
            <Route path="tools" element={<SupplierToolsPage />} />
            <Route path="analytics" element={<SellerAnalyticsPage />} />
            
            {/* RFQ Routes */}
            <Route path="rfq" element={<RFQListPage />} />
            <Route path="rfq/create" element={<CreateRFQPage />} />
            <Route path="rfq/:id" element={<RFQDetailPage />} />
            
            {/* Order Routes */}
            <Route path="orders" element={<OrdersListPage />} />
            <Route path="orders/create" element={<CreateOrderPage />} />
            <Route path="orders/:id" element={<OrderDetailPage />} />
            
            {/* Disputes */}
            <Route path="disputes" element={<DisputesPage />} />
          </Route>

          {/* Settings Page */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}><SettingsPage /></Suspense>
              </ProtectedRoute>
            }
          />

          {/* Admin Portal - /backend-login with /backend alias */}
          {["/backend-login", "/backend"].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}><AdminDashboardPage /></Suspense>
                </ProtectedRoute>
              }
            />
          ))}

          {/* 404 Page - Must be inside PublicLayout for header/footer */}
          <Route element={<PublicLayout />}>
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
          </VisitorTracker>
        </BrowserRouter>
      </CurrencyProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
